import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PrinterConnection, PrinterProtocol } from '@/lib/shared-types';
import { useRequireScope } from '@/Hooks/useRequireScope';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  FLEET_ROUTES,
  connectionNeedsBridge,
  type CreatePrinterBody,
  type FleetBridge,
  type FleetPrinter,
} from '../fleetApi';
import { apiMessage, type BranchOption } from '../fleetHelpers';

const CONNECTIONS = Object.values(PrinterConnection);
const PROTOCOLS = Object.values(PrinterProtocol);

/**
 * A printer profile: what the hardware IS and where it lives.
 *
 * Not what it is doing — `lastStatus` arrives from the bridge as a self-report
 * and is never set from here, because an inferred status is how a dashboard
 * comes to claim a printer is online on the strength of a job being queued for
 * it.
 *
 * Two rules are enforced client-side because the server enforces them and an
 * operator should meet them while they are still looking at the form:
 *
 *  - **A `BRIDGE_*` printer must name a bridge.** Without one it is a printer no
 *    job can ever reach, and the failure shows up hours later as tickets
 *    silently queueing forever. The API answers `PRINTER_NEEDS_BRIDGE`; this
 *    says the same thing before the round trip.
 *  - **The bridge must be in the same branch**, because a bridge across the city
 *    cannot open a socket to this venue's LAN. So the picker only offers the
 *    bridges of the branch being edited.
 *
 * `code` is uppercase and unique per branch: an operator names a printer in a
 * runbook or a CSV import by this rather than by a cuid, so it has to be
 * something a person can type without getting it subtly wrong.
 */
export default function PrinterFormDialog({
  open,
  printer,
  branches,
  bridges,
  defaultBranchId,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. Editing cannot move a printer's branch or change its code. */
  printer: FleetPrinter | null;
  branches: BranchOption[];
  bridges: FleetBridge[];
  defaultBranchId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const requireScope = useRequireScope();

  const [branchId, setBranchId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [connection, setConnection] = useState<PrinterConnection>('BRIDGE_LAN');
  const [protocol, setProtocol] = useState<PrinterProtocol>('ESC_POS');
  const [bridgeId, setBridgeId] = useState('');
  const [address, setAddress] = useState('');
  const [port, setPort] = useState('');
  const [paperWidthMm, setPaperWidthMm] = useState('');
  const [charactersPerLine, setCharactersPerLine] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBranchId(printer?.branchId ?? defaultBranchId ?? branches[0]?.id ?? '');
    setName(printer?.name ?? '');
    setCode(printer?.code ?? '');
    setConnection(printer?.connection ?? 'BRIDGE_LAN');
    setProtocol(printer?.protocol ?? 'ESC_POS');
    setBridgeId(printer?.bridgeId ?? '');
    setAddress(printer?.address ?? '');
    setPort(printer?.port ? String(printer.port) : '');
    setPaperWidthMm(printer ? String(printer.paperWidthMm) : '');
    setCharactersPerLine(printer ? String(printer.charactersPerLine) : '');
  }, [open, printer, defaultBranchId, branches]);

  const branchBridges = useMemo(
    () => bridges.filter((bridge) => bridge.branchId === branchId && bridge.isActive),
    [bridges, branchId],
  );

  const needsBridge = connectionNeedsBridge(connection);
  const codeValid = !code || /^[A-Z0-9_-]+$/.test(code);
  const outOfScope = !printer && Boolean(branchId) && !requireScope('BRANCH', branchId);

  const problem = (): string | null => {
    if (!name.trim()) return t('fleet.printers.nameRequired');
    if (!printer && !branchId) return t('fleet.printers.branchRequired');
    if (!printer && !code.trim()) return t('fleet.printers.codeRequired');
    if (!codeValid) return t('fleet.printers.codeFormat');
    if (needsBridge && !bridgeId) return t('fleet.printers.bridgeRequired');
    return null;
  };

  const submit = async () => {
    const invalid = problem();
    if (invalid) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const shared = {
        name: name.trim(),
        connection,
        protocol,
        // Explicitly null rather than omitted, so switching a printer from LAN
        // to cloud actually detaches its bridge. `PrintersService.update` only
        // reads the field when it is present.
        bridgeId: needsBridge ? bridgeId : undefined,
        address: address.trim() || undefined,
        port: port.trim() ? Number(port) : undefined,
        paperWidthMm: paperWidthMm.trim() ? Number(paperWidthMm) : undefined,
        charactersPerLine: charactersPerLine.trim() ? Number(charactersPerLine) : undefined,
      };
      if (printer) {
        await api.patch(FLEET_ROUTES.printer(printer.id), shared);
        toast.success(t('fleet.printers.updated'));
      } else {
        const body: CreatePrinterBody = { ...shared, branchId, code: code.trim() };
        await api.post(FLEET_ROUTES.printers, body);
        toast.success(t('fleet.printers.created'));
      }
      onSaved();
      onClose();
    } catch (requestError) {
      setError(apiMessage(requestError, t('fleet.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={printer ? t('fleet.printers.editTitle') : t('fleet.printers.addTitle')}
      description={t('fleet.printers.formHint')}
      className="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy || outOfScope} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {!printer && (
          <Field
            label={t('fleet.common.branch')}
            required
            error={outOfScope ? t('fleet.printers.branchOutOfScope') : undefined}
          >
            <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
              <option value="">{t('fleet.printers.chooseBranch')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('fleet.common.name')} required>
          <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} />
        </Field>

        {!printer && (
          <Field
            label={t('fleet.printers.code')}
            required
            hint={t('fleet.printers.codeHint')}
            error={codeValid ? undefined : t('fleet.printers.codeFormat')}
          >
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              maxLength={32}
              placeholder="KITCHEN1"
            />
          </Field>
        )}

        <Field label={t('fleet.printers.connection')} hint={t('fleet.printers.connectionHint')}>
          <Select
            value={connection}
            onChange={(event) => setConnection(event.target.value as PrinterConnection)}
          >
            {CONNECTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.connection.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('fleet.printers.protocol')}>
          <Select
            value={protocol}
            onChange={(event) => setProtocol(event.target.value as PrinterProtocol)}
          >
            {PROTOCOLS.map((option) => (
              <option key={option} value={option}>
                {t(`fleet.protocol.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        {needsBridge && (
          <Field
            label={t('fleet.common.bridge')}
            required
            hint={t('fleet.printers.bridgeHint')}
            error={bridgeId ? undefined : t('fleet.printers.bridgeRequired')}
          >
            <Select value={bridgeId} onChange={(event) => setBridgeId(event.target.value)}>
              <option value="">{t('fleet.printers.chooseBridge')}</option>
              {branchBridges.map((bridge) => (
                <option key={bridge.id} value={bridge.id}>
                  {bridge.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('fleet.printers.address')} hint={t('fleet.printers.addressHint')}>
          <Input
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            maxLength={200}
            placeholder="192.168.1.50"
          />
        </Field>

        <Field label={t('fleet.printers.port')}>
          <Input
            value={port}
            inputMode="numeric"
            onChange={(event) => setPort(event.target.value.replace(/\D/g, ''))}
            placeholder="9100"
          />
        </Field>

        <Field label={t('fleet.printers.paperWidth')} hint={t('fleet.printers.paperWidthHint')}>
          <Input
            value={paperWidthMm}
            inputMode="numeric"
            onChange={(event) => setPaperWidthMm(event.target.value.replace(/\D/g, ''))}
            placeholder="80"
          />
        </Field>

        <Field
          label={t('fleet.printers.charactersPerLine')}
          hint={t('fleet.printers.charactersPerLineHint')}
        >
          <Input
            value={charactersPerLine}
            inputMode="numeric"
            onChange={(event) => setCharactersPerLine(event.target.value.replace(/\D/g, ''))}
            placeholder="42"
          />
        </Field>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </Dialog>
  );
}
